import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Box, TextField, Button, Typography } from "@mui/material";
import siteLogo from "../assets/logo-rpo-white-top.png";
import "./Login.css";
import { fetchRegisterAccess } from "../utils/registerAccess";
import { logoutUser, verifyStoredAuthSession } from "../controllers/authController";

/**
 * CRA API base
 * Dev:  REACT_APP_API_BASE=http://localhost:5000/api
 * Prod: REACT_APP_API_BASE=/rpo/api
 */
const API_BASE = process.env.REACT_APP_API_BASE;

console.log(`API_BASE: ${API_BASE}\n Env: ${process.env.NODE_ENV}`);

function isBadToken(token) {
  return !token || token === "null" || token === "undefined";
}

function safeSetAuth(token, user) {
  let cleaned = String(token || "").trim();
  if (cleaned.toLowerCase().startsWith("bearer ")) {
    cleaned = cleaned.slice(7).trim();
  }

  if (isBadToken(cleaned)) return false;

  localStorage.setItem("token", cleaned);
  if (user) localStorage.setItem("user", JSON.stringify(user));
  return true;
}

function clearAuth() {
  [localStorage, sessionStorage].forEach((storage) => {
    storage.removeItem("token");
    storage.removeItem("authToken");
    storage.removeItem("user");
  });
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState(location.state?.message || "");
  const [canRegister, setCanRegister] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [retryAt, setRetryAt] = useState(0);
  const [retrySeconds, setRetrySeconds] = useState(0);
  const [mfa, setMfa] = useState(null);
  const [mfaCode, setMfaCode] = useState("");
  const isLocal = ["localhost", "127.0.0.1", "[::1]"].includes(window.location.hostname);
  const authenticatorAccount = `${isLocal ? "RPO Local" : "RPO"} (${email})`;

  useEffect(() => {
    if (!retryAt) return;
    const update = () => {
      const remaining = Math.max(0, Math.ceil((retryAt - Date.now()) / 1000));
      setRetrySeconds(remaining);
      if (!remaining) {
        setRetryAt(0);
        setErrorMessage("");
      }
    };
    update();
    const timer = setInterval(update, 1000);
    return () => clearInterval(timer);
  }, [retryAt]);

  useEffect(() => {
    let active = true;

    const run = async () => {
      const hasStoredSession = Boolean(
        localStorage.getItem("token") || localStorage.getItem("authToken") || localStorage.getItem("user")
          || sessionStorage.getItem("token") || sessionStorage.getItem("authToken") || sessionStorage.getItem("user")
      );

      if (!hasStoredSession) {
        if (active) setCheckingSession(false);
        return;
      }

      const verifiedUser = await verifyStoredAuthSession();

      if (!active) return;

      if (verifiedUser) {
        navigate("/overview", { replace: true });
        return;
      }

      await logoutUser();
      setErrorMessage(location.state?.message || "Your session expired. Please log in again.");
      setCheckingSession(false);
    };

    run();

    return () => {
      active = false;
    };
  }, [location.state?.message, navigate]);

  useEffect(() => {
    let active = true;

    fetchRegisterAccess()
      .then((allowed) => {
        if (active) setCanRegister(allowed);
      })
      .catch(() => {
        if (active) setCanRegister(false);
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (location.state?.message) {
      setErrorMessage(location.state.message);
    }
  }, [location.state]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (checkingSession || submitting || retryAt > Date.now()) return;
    setErrorMessage("");

    if (!mfa && (!email || !password)) {
      setErrorMessage("Email and Password are required.");
      return;
    }

    setSubmitting(true);
    try {
      const response = mfa
        ? await axios.post(`${API_BASE}/mfa/verify`, { challenge: mfa.challenge, code: mfaCode }, { withCredentials: true, timeout: 20000 })
        : await axios.post(`${API_BASE}/login`, { email, password }, { withCredentials: true, timeout: 20000 });
      if (response.data?.mfaRequired) { setMfa(response.data); setPassword(""); return; }
      const { token, user } = response.data || {};

      const ok = safeSetAuth(token, user);
      if (!ok) {
        clearAuth();
        setErrorMessage("Login failed: server returned an invalid token.");
        return;
      }

      navigate("/overview");
    } catch (err) {
      const msg =
        (err.code === "ECONNABORTED" || err.code === "ETIMEDOUT"
          ? "Sign-in timed out. Please try again." : "") ||
        err.response?.data?.error ||
        err.response?.data?.message ||
        "An error occurred during login.";

      clearAuth();
      setErrorMessage(msg);
      if (err.response?.status === 429) {
        const seconds = Number(err.response.data?.retryAfter);
        const wait = Number.isFinite(seconds) && seconds > 0 ? Math.min(seconds, 86400) : 60;
        setRetrySeconds(Math.ceil(wait));
        setRetryAt(Date.now() + wait * 1000);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box component="main" className="rpo-login" aria-busy={checkingSession}>
      <Box className="rpo-login-content">
        <img src={siteLogo} className="rpo-login-logo" alt="RPO.AI" />
        <Typography component="h1" className="rpo-login-heading">AI Recruiting Operating System</Typography>
        <Typography className="rpo-login-intro">Login using email address</Typography>
        <Box component="form" method="post" onSubmit={handleLogin} className={`rpo-login-form${mfa ? " rpo-login-form-mfa" : ""}`}>
          <Typography component="label" htmlFor="login-email" className="rpo-login-label">Email</Typography>
              <TextField
                id="login-email"
                placeholder="Email"
                fullWidth
                variant="outlined"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />

              <Typography component="label" htmlFor="login-password" className="rpo-login-label">Password</Typography>
              <TextField
                id="login-password"
                placeholder="Password"
                fullWidth
                variant="outlined"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required={!mfa}
                disabled={Boolean(mfa)}
              />
              {mfa && <Box>
                <Typography color="white" sx={{ fontWeight: 700 }}>Two-factor authentication with Google Authenticator</Typography>
                {isLocal && <Typography color="white" sx={{ my: 2 }}>Use the RPO Local entry in Google Authenticator. Local development has a separate setup key from production.</Typography>}
                {mfa.enrollmentRequired ? <Box component="ol" sx={{ color: "white", pl: 3 }}>
                  <li>Install Google Authenticator from Google Play or the Apple App Store on your phone.</li>
                  <li>Open the app, tap +, and choose “Enter a setup key”.</li>
                  <li>Use {authenticatorAccount} as the account name, enter the setup key below, choose “Time based”, and tap Add.</li>
                  <li>Enter the six-digit code shown for your RPO account below to finish signing in.</li>
                </Box> : <Typography color="white" sx={{ my: 2 }}>Open Google Authenticator on your phone, find your RPO account, and enter its current six-digit code below. Use a new code if the previous one expired or was already used.</Typography>}
                {mfa.enrollmentSecret && <Typography color="white">Setup key — keep this private:</Typography>}
                {mfa.enrollmentSecret && <Typography component="code" sx={{ display: "block", wordBreak: "break-all", color: "white", my: 2 }}>{mfa.enrollmentSecret}</Typography>}
                <Typography sx={{ mb: 2 }}><a style={{ color: "white" }} href="https://support.google.com/accounts/answer/1066447" target="_blank" rel="noopener noreferrer">Google Authenticator help</a></Typography>
                <TextField placeholder="Verification code" inputProps={{ "aria-label": "Verification code", inputMode: "numeric", pattern: "[0-9]{6}", maxLength: 6 }} autoComplete="one-time-code" value={mfaCode} onChange={event => setMfaCode(event.target.value.replace(/\D/g, "").slice(0, 6))} required />
                <Button onClick={() => { setMfa(null); setMfaCode(""); setPassword(""); }}>Start again</Button>
              </Box>}

              <Button type="submit" className="rpo-login-submit" variant="outlined" disabled={checkingSession || submitting || retrySeconds > 0}
                sx={{ "&.Mui-disabled": { color: "#475467 !important", backgroundColor: "#EAECF0 !important" } }}>
                {retrySeconds > 0
                  ? `Try again in ${Math.floor(retrySeconds / 60)}:${String(retrySeconds % 60).padStart(2, "0")}`
                  : submitting ? "Signing in…" : "Log in"}
              </Button>

              {errorMessage && <Typography color="error" role="alert"
                sx={{ color: "#B42318 !important", backgroundColor: "#FEF3F2", borderRadius: 2, padding: 1.5 }}>
                {errorMessage}
              </Typography>}

              <Typography variant="body2" sx={{ color: "white", textAlign: "center" }}>
                <Link to="/forgot-password">
                  Forgot your password?
                </Link>
              </Typography>

        </Box>
        {canRegister && <Typography className="rpo-login-signup">
          Need to create an account? <Link to="/register">Sign-Up</Link>
        </Typography>}
      </Box>
    </Box>
  );
}
